import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, setUnauthorizedHandler } from "./lib/api";
import type { EvaluationPatch, FilterPreset, Interaction, School, Settings } from "./lib/types";

interface AppState {
  ready: boolean;
  authed: boolean;
  admin: boolean;
  schools: School[];
  settings: Settings | null;
  filterPresets: FilterPreset[];
  error: string | null;
  byId: (id: string) => School | undefined;
  login: (password: string) => Promise<{ authed: boolean; admin: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  patchSchool: (id: string, patch: Partial<School>) => Promise<void>;
  putEvaluation: (id: string, ev: EvaluationPatch) => Promise<void>;
  addInteraction: (id: string, e: Partial<Interaction>) => Promise<void>;
  patchInteraction: (id: string, iid: string, e: Partial<Interaction>) => Promise<void>;
  deleteInteraction: (id: string, iid: string) => Promise<void>;
  putSettings: (s: Partial<Settings>) => Promise<void>;
  saveFilterPresets: (presets: FilterPreset[]) => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const idIndex = useRef(new Map<string, number>());

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthed(false);
      setAdmin(false);
    });
  }, []);

  const reindex = useCallback((list: School[]) => {
    idIndex.current = new Map(list.map((s, i) => [s.id, i]));
  }, []);

  const loadData = useCallback(async () => {
    const [{ schools: list, settings: s }, { presets }] = await Promise.all([
      api.schools(),
      api.filterPresets(),
    ]);
    setSchools(list);
    reindex(list);
    setSettings(s);
    setFilterPresets(presets);
  }, [reindex]);

  const refresh = useCallback(async () => {
    try {
      const { authed: a, admin: ad } = await api.session();
      setAuthed(a);
      setAdmin(ad);
      if (a) await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "load failed");
    } finally {
      setReady(true);
    }
  }, [loadData]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const spliceSchool = useCallback((school: School) => {
    setSchools((prev) => {
      const i = prev.findIndex((s) => s.id === school.id);
      if (i < 0) return prev;
      const next = prev.slice();
      next[i] = school;
      return next;
    });
  }, []);

  const login = useCallback(
    async (password: string) => {
      try {
        const { authed: a, admin: ad } = await api.login(password);
        setAuthed(a);
        setAdmin(ad);
        if (a) await loadData();
        return { authed: a, admin: ad };
      } catch {
        return { authed: false, admin: false };
      }
    },
    [loadData],
  );

  const logout = useCallback(async () => {
    await api.logout();
    setAuthed(false);
    setAdmin(false);
    setSchools([]);
    setFilterPresets([]);
  }, []);

  const value = useMemo<AppState>(
    () => ({
      ready,
      authed,
      admin,
      schools,
      settings,
      filterPresets,
      error,
      byId: (id) => {
        const i = idIndex.current.get(id);
        return i === undefined ? undefined : schools[i];
      },
      login,
      logout,
      refresh: loadData,
      patchSchool: async (id, patch) => spliceSchool((await api.patchSchool(id, patch)).school),
      putEvaluation: async (id, ev) => spliceSchool((await api.putEvaluation(id, ev)).school),
      addInteraction: async (id, e) => {
        const { interactions } = await api.addInteraction(id, e);
        setSchools((prev) => prev.map((s) => (s.id === id ? { ...s, interactions } : s)));
      },
      patchInteraction: async (id, iid, e) => {
        const { interactions } = await api.patchInteraction(id, iid, e);
        setSchools((prev) => prev.map((s) => (s.id === id ? { ...s, interactions } : s)));
      },
      deleteInteraction: async (id, iid) => {
        const { interactions } = await api.deleteInteraction(id, iid);
        setSchools((prev) => prev.map((s) => (s.id === id ? { ...s, interactions } : s)));
      },
      putSettings: async (s) => setSettings((await api.putSettings(s)).settings),
      saveFilterPresets: async (presets) =>
        setFilterPresets((await api.putFilterPresets(presets)).presets),
    }),
    [ready, authed, admin, schools, settings, filterPresets, error, login, logout, loadData, spliceSchool],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp outside AppProvider");
  return v;
}
